// Playback of a trimmed sound clip in the editor and the preview, the same way the export plays it
// (toons-render-worker/src/timeline.js + ffmpeg.js):
//   - the clip starts `offsetSec` into its file (sound layers: startSound; voices: TTSTrimStart)
//   - "loop" on a sound layer repeats the clip after its bar ends, until the end of the slide
//     (the timeline starts each repeat); `loop` here only means "loop the whole file"
//   - fade in over the first `fadeIn` seconds of the clip, fade out over the last `fadeOut` seconds

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** 0..1 volume factor for fades, `sec` seconds into a clip that lasts `clipSec`. */
export function fadeFactor(fadeIn, fadeOut, sec, clipSec) {
  let f = 1;
  const fi = num(fadeIn);
  const fo = num(fadeOut);
  if (fi > 0 && sec < fi) f = Math.min(f, Math.max(0, sec / fi));
  if (fo > 0 && clipSec > 0 && sec > clipSec - fo) f = Math.min(f, Math.max(0, (clipSec - sec) / fo));
  return f;
}

/** Position in the file, `sec` seconds into the clip. */
export function filePosition(offsetSec, sec, fileSec, loop) {
  const pos = num(offsetSec) + Math.max(0, sec);
  if (!loop || !(fileSec > 0) || pos < fileSec) return pos;
  return (pos - fileSec) % fileSec;
}

/**
 * Starts `audio` for a clip: seeks, sets the loop and volume, plays, and keeps the fades right
 * while it plays (reading the play position from `secNow()`). Returns a function that stops the
 * fade updates. `volume` is 0..1. `duck()` (optional) gives the ducking factor (Utils/ducking.js).
 */
export function playClip(audio, { offsetSec, sec, clipSec, loop, fadeIn, fadeOut, volume, secNow, duck }) {
  if (!audio) return () => {};
  const vol = Math.min(1, Math.max(0, volume === undefined || volume === null ? 1 : volume));
  audio.loop = !!loop;
  const fileSec = Number.isFinite(audio.duration) ? audio.duration : 0;
  const at = filePosition(offsetSec, sec, fileSec, loop);
  if (fileSec > 0 && at >= fileSec) return () => {}; // the clip is past the end of the file
  try {
    audio.currentTime = at;
  } catch (e) {
    /* not loaded yet: plays from the start */
  }
  const setVolume = (s) => {
    const d = typeof duck === "function" ? duck() : 1;
    audio.volume = Math.min(1, Math.max(0, vol * d * fadeFactor(fadeIn, fadeOut, s, clipSec)));
  };
  setVolume(sec);
  const p = audio.play();
  if (p && p.catch) p.catch(() => {});
  if (!(num(fadeIn) > 0 || num(fadeOut) > 0 || typeof duck === "function") || typeof secNow !== "function") return () => {};
  const timer = setInterval(() => {
    if (audio.paused) {
      clearInterval(timer);
      return;
    }
    setVolume(secNow());
  }, 50);
  return () => clearInterval(timer);
}

/** Seconds from a "HH:MM:SS:mmm" time (or a number of seconds). */
export function timeToSeconds(t) {
  if (typeof t === "number") return t;
  if (!t) return 0;
  const a = String(t).split(":").map(Number);
  if (a.length === 4) return a[0] * 3600 + a[1] * 60 + a[2] + a[3] / 1000;
  if (a.length === 3) return a[0] * 3600 + a[1] * 60 + a[2];
  return num(t);
}

/**
 * A character's voice (Text to Speech) after trimming: { start, end, length } in seconds of the
 * voice file. TTSTrimStart / TTSTrimEnd are optional; without them the whole voice plays.
 */
export function voiceTrim(layer) {
  const full = timeToSeconds(layer && layer.TTSDuration);
  let start = Math.max(0, num(layer && layer.TTSTrimStart));
  let end = layer && layer.TTSTrimEnd !== undefined && layer.TTSTrimEnd !== null ? num(layer.TTSTrimEnd) : full;
  if (full > 0) {
    start = Math.min(start, full);
    end = Math.min(end || full, full);
  }
  if (end <= start) end = full > start ? full : start;
  return { start, end, length: Math.max(0, end - start), full };
}

/** "HH:MM:SS:mmm" from seconds (the app's time format). */
export function secondsToTime(sec) {
  const total = Math.max(0, Math.round(num(sec) * 1000));
  const pad = (n, z = 2) => String(n).padStart(z, "0");
  const ms = total % 1000;
  const s = Math.floor(total / 1000);
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}:${pad(ms, 3)}`;
}

/**
 * A sound bar's edge was dragged on the timeline (all values in timeline px).
 * Left edge: the clip starts later (or earlier) in its file by exactly as much as the edge moved,
 * never before the start of the file. Right edge: only the length changes. The bar never runs
 * past the end of the file (`filePx`, 0 = unknown).
 * Returns { offset, start, width }.
 */
export function trimSoundBar({ offset, start, dir, newStart, newWidth, filePx }) {
  let o = num(offset);
  let s = num(start);
  let w = num(newWidth);
  if (dir === "left") {
    o += num(newStart) - s;
    s = num(newStart);
    if (o < 0) {
      s -= o;
      w += o;
      o = 0;
    }
  }
  if (filePx > 0 && o + w > filePx) w = filePx - o;
  return { offset: o, start: s, width: Math.max(1, w) };
}
